/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { i18n } from '../../../translate/i18n'
import { ProgressTracker } from '../../../components/ProgressTracker/ProgressTracker'
import { PageTitle } from '../../../../_metronic/layout/core'
import { BreadCrumbs } from '../../../components/BreadCrumbs/breadCrumbrs'
import { KTCard, KTCardBody } from '../../../../_metronic/helpers'
import { GET, POST, POST_ADM } from '../../../services/api'
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { Pie, Line } from 'react-chartjs-2'
import './fileManager.module.css'

// Registre os componentes do Chart.js
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
)

interface FileItem {
  key: string;
  size: number;
  lastModified: string;
  etag: string;
}

interface FolderContentsResponse {
  data: {
    files: FileItem[];
    totalItems: number;
    folderPath: string;
  };
  success: boolean;
  typemsg: string;
  message: string;
  metadata: {
    bucket: string;
    folder: string;
    totalItems: number;
  };
}

// Interface para arquivos selecionados para upload
interface SelectedFile {
  file: File;
  id: string;
  progress: number;
  uploaded: boolean;
}

interface DataSourceResponse {
  success: boolean;
  data: {
    id: string;
    prefix: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  }[];
  message: string;
  typemsg: string;
}

// Adicione interfaces para tipagem
interface Statistics {
  numberOfDocumentsScanned: number;
  numberOfMetadataDocumentsScanned: number;
  numberOfNewDocumentsIndexed: number;
  numberOfModifiedDocumentsIndexed: number;
  numberOfMetadataDocumentsModified: number;
  numberOfDocumentsDeleted: number;
  numberOfDocumentsFailed: number;
}

interface Job {
  ingestionJobId: string;
  status: string;
  startedAt: string;
  completedAt: string;
  statistics: Statistics;
}

interface DataSource {
  dataSourceId: string;
  dataSourceName: string;
  status: string;
  jobs: Job[];
}

interface JobTimeData {
  x: number; // timestamp
  y: number; // número de documentos
}

// Interface para as estatísticas totais
interface TotalStats {
  scanned: number;
  indexed: number;
  modified: number;
  deleted: number;
  failed: number;
}

type PluginconfigType = {
  id: number
  name: string
  content: any
  plugintemplateId: number
  plugintypeId: number
}

type TemplateType = {
  id: number
  name: string
  image: string | null
  parameters: string
  plugintypeId: number
  pluginconfig: PluginconfigType
  typeIntegration: string
  able: boolean | null
}


const FileManagerFilesPage: React.FC = () => {
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);

  useEffect(() => {
    if (i18n.isInitialized) {
      setIsI18nInitialized(true);
    } else {
      i18n.on('initialized', () => {
        setIsI18nInitialized(true);
      });
    }
  }, []);

  const navigate = useNavigate()
  const [config, setConfig] = useState<PluginconfigType | null>(null)
  const [sortField, setSortField] = useState<string>('lastModified')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [files, setFiles] = useState<FileItem[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [totalSize, setTotalSize] = useState(0)
  const [loading, setLoading] = useState(false)
  const [agentName, setAgentName] = useState('')
  const [folderPath] = useState('training/ninegrid')
  const [bucketname] = useState('iagenerative.ninegrid.com') // Nome do bucket, pode ser dinâmico se necessário
  const [knowledgeBaseName, setKnowledgeBaseName] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  // Usado para selecionar todos os itens
  const [selectedItems, setSelectedItems] = useState<string[]>([])


  // Estados para upload de arquivos
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Estados iniciais da páginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // Estado para monitorar o processo de sincronização
  const [syncProcessId, setSyncProcessId] = useState<string | null>(null);
  useEffect(() => {
    return () => {
      setSyncProcessId(null);
    };
  }, []);

  // Função para extrair nome do arquivo da key
  const getFileName = (key: string): string => {
    return key.split('/').pop() || key
  }

  // Adicione esta const após as outras constantes no início do componente
  const filteredFiles = files.filter((file) => {
    const fileName = getFileName(file.key).toLowerCase()
    const search = searchTerm.toLowerCase()
    return fileName.includes(search)
  })

  // Função para ordenar os arquivos
  const sortFiles = useCallback((files: FileItem[]) => {
    return [...files].sort((a, b) => {
      if (sortField === 'key') {
        return sortOrder === 'asc' 
          ? getFileName(a.key).localeCompare(getFileName(b.key))
          : getFileName(b.key).localeCompare(getFileName(a.key))
      }
      
      if (sortField === 'size') {
        return sortOrder === 'asc' 
          ? a.size - b.size 
          : b.size - a.size
      }
      
      if (sortField === 'lastModified') {
        return sortOrder === 'asc'
          ? new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime()
          : new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
      }
      
      return 0
    })
  }, [sortField, sortOrder])


  // Funções para controle da paginação
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const sortedFiles = sortFiles(filteredFiles)
  const currentFiles = sortedFiles.slice(indexOfFirstItem, indexOfLastItem)
  //const currentFiles = sortedFiles.slice(indexOfFirstItem, indexOfLastItem)
  //const totalPages = Math.ceil(files.length / itemsPerPage)
  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage)
 

  // Tipos de arquivos permitidos
  const ALLOWED_FILE_TYPES = [
    'text/plain', // TXT
    'application/msword', // DOC
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
    'text/csv', // CSV
    'application/vnd.ms-excel', // XLS
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
    'image/jpeg', // JPG/JPEG
    'image/png', // PNG
    'application/pdf', // PDF
  ]

  // Extensões de arquivo permitidas (fallback)
  const ALLOWED_EXTENSIONS = [
    '.txt', '.doc', '.docx', '.csv', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.pdf'
  ]

  // Tamanho máximo total: 50MB
  const MAX_TOTAL_SIZE = 50 * 1024 * 1024 // 50MB em bytes

  // Função para validar tipo de arquivo
  const isValidFileType = (file: File): boolean => {
    // Verifica pelo MIME type
    if (ALLOWED_FILE_TYPES.includes(file.type)) {
      return true
    }
    
    // Fallback: verifica pela extensão
    const fileName = file.name.toLowerCase()
    return ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext))
  }

  // Função para calcular tamanho total dos arquivos selecionados
  const calculateTotalSize = (files: SelectedFile[]): number => {
    return files.reduce((total, selectedFile) => total + selectedFile.file.size, 0)
  }

  // Função para processar arquivos selecionados
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const newFiles: SelectedFile[] = []
    const invalidFiles: string[] = []

    // Valida cada arquivo
    Array.from(files).forEach(file => {
      // Verifica tipo de arquivo
      if (!isValidFileType(file)) {
        invalidFiles.push(file.name)
        return
      }

      newFiles.push({
        file,
        id: Math.random().toString(36).substr(2, 9),
        progress: 0,
        uploaded: false
      })
    })

    // Se há arquivos inválidos, mostra erro
    if (invalidFiles.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Arquivos não permitidos',
        text: `Os seguintes arquivos não são permitidos: ${invalidFiles.join(', ')}. Tipos permitidos: TXT, DOC, DOCX, PDF, CSV, XLS, XLSX, JPG, JPEG, PNG`,
      })
    }

    // Se não há arquivos válidos, para aqui
    if (newFiles.length === 0) {
      // Limpa o input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    // Verifica se o tamanho total não excede 50MB
    const currentTotalSize = calculateTotalSize(selectedFiles)
    const newTotalSize = calculateTotalSize(newFiles)
    const finalTotalSize = currentTotalSize + newTotalSize

    if (finalTotalSize > MAX_TOTAL_SIZE) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao processar os arquivos: excede o limite de 50MB',
      })
      
      // Limpa o input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }
    
    // Adiciona os arquivos válidos
    setSelectedFiles(prev => [...prev, ...newFiles])
    
    // Limpa o input para permitir selecionar os mesmos arquivos novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Função para formatar bytes em unidades legíveis
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  

  // Função para formatar data
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

// Adicione esta função para checar o status
const checkSyncStatus = async (processId: string) => {
  try {
    const response = await POST('/knowledge-base/syncCheckStatus', {
      processId: processId
    });

    //Incluir um timeout para evitar loops infinitos
    if (!response || typeof response !== 'object') {
      throw new Error('Resposta inválida do servidor');
    }

    //imcluir um timer 
    if (!response.success && response.message === 'Processo não encontrado') {
      return null; // Retorna null se o processo não for encontrado
    }

    // Verifica se a resposta é bem-sucedida




    if (!response.success) {
      throw new Error(response.message || 'Erro ao verificar status');
    }

    // Log para debug da resposta completa
    console.log('Response completa:', JSON.stringify(response, null, 2));

    // Verifica se response.data existe e extrai os dados corretamente
    const data = response.data || {};

    // Log para debug
    console.log('Status response:', data);

    await new Promise((resolve) => setTimeout(resolve, 2000)); // Aguarda 2 segundos antes de retornar

    // Mapeia a estrutura da resposta para o formato esperado
    return {
      progress: data.progress || 0,
      currentStep: data.status || '', 
      details: data.result?.message || '',
      completed: data.status === "completed",
      error: data.error?.toString() || null, // Garante que error será string ou null
      result: data.result || null
    };

  } catch (error) {
    console.error('Erro ao verificar status:', error);
    return null;
  }
};

  // Função para sincronizar base de conhecimento de IA
const syncfolders3IA = useCallback(async () => {
  setLoading(true);
  try {
    // Validar se o knowledgeBaseName está definido
    if (!knowledgeBaseName) {
      throw new Error('Nome da base de conhecimento não definido');
    }

    // Prepara o payload com apenas os dados necessários
    const payload = {
      knowledgeBaseName: knowledgeBaseName,
      bucketPrefix: folderPath,
    };

    // Faz a requisição com o payload limpo
    const response = await POST('/knowledge-base/sync-folderS3', payload);

    // Log para debug sem estruturas circulares
    console.log('Response:', {
      success: response.success,
      processId: response.processId,
      message: response.message
    });

    if (!response.success) {
      throw new Error(response.message || 'Erro ao sincronizar base de conhecimento');
    }

    const processId = response.processId;
    if (!processId) {
      throw new Error('ID do processo não retornado');
    }

    // Define o processId para iniciar o monitoramento
    setSyncProcessId(processId);

    // Mostra o modal de progresso
    const syncModal = await Swal.fire({
      title: 'Sincronismo em andamento',
      html: `
        <div id="sync-status-message">
          Realizando análise do repositório e sincronizando base de conhecimento...
        </div>
        <div class="progress mt-3" style="height: 20px;">
          <div 
            id="sync-progress-bar"
            class="progress-bar progress-bar-striped progress-bar-animated" 
            role="progressbar" 
            style="width: 0%"
            aria-valuenow="0"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span id="progress-percentage">0%</span>
          </div>
        </div>
        <div id="sync-status-details" class="mt-2">
          <small class="text-muted">Processo iniciado...</small>
        </div>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: async () => {
        try {
          const progressBar = document.getElementById('sync-progress-bar');
          const messageElement = document.getElementById('sync-status-message');
          const detailsElement = document.getElementById('sync-status-details');
          const percentageElement = document.getElementById('progress-percentage');

          let isProcessing = true;
          while (isProcessing) {
            const status = await checkSyncStatus(processId);
            
            if (!status) {
              throw new Error('Não foi possível continuar com a sincronização');
            }

            if (progressBar && messageElement && detailsElement && percentageElement) {
              const progress = status.progress || 0;
              progressBar.style.width = `${progress}%`;
              progressBar.setAttribute('aria-valuenow', progress.toString());
              percentageElement.textContent = `${progress}%`;

              if (status.currentStep) {
                messageElement.textContent = status.currentStep;
              }

              if (status.details) {
                detailsElement.innerHTML = `<small class="text-muted">${status.details}</small>`;
              }

              if (status.completed || status.currentStep === "completed") {
                progressBar.style.width = '100%';
                progressBar.classList.remove('progress-bar-animated');
                percentageElement.textContent = '100%';
                
                const successMessage = status.result?.message || 'Sincronização concluída com sucesso!';
                messageElement.textContent = successMessage;
                
                isProcessing = false;
                
                setTimeout(() => {
                  Swal.close();
                  fetchFolderContents();
                }, 1500);
                break;
              }

              if (status.error) {
                throw new Error(status.error);
              }
            }

            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.error('Erro durante o processamento:', error);
          Swal.close();
          throw error;
        }
      }
    });

    await syncModal;

  } catch (error) {
    console.error('Erro ao sincronizar:', error);
    Swal.fire({
      icon: 'error',
      title: 'Erro ao sincronizar',
      text: 'Falha na sincronização da base de conhecimento',
      timer: 3000,
    });
  } finally {
    setLoading(false);
    setSyncProcessId(null);
  }
}, [knowledgeBaseName, folderPath]);


// Função para buscar conteúdo da pasta
  const fetchFolderContents = useCallback(async () => {
    setLoading(true)
    try {
      const response: FolderContentsResponse = await POST('/manager-file/listFolderContents', {
        folderPath: folderPath,
      })

      if (!response.success) {
        throw new Error(response.message || 'Erro ao buscar arquivos')
      }
      
      setFiles(response.data.files)
      setTotalItems(response.data.totalItems)
      
      // Calcula o tamanho total somando TODOS os sizes dos arquivos
      const totalSizeBytes = response.data.files.reduce(
        (sum: number, file: FileItem) => sum + file.size, 
        0
      )
      setTotalSize(totalSizeBytes)

    } catch (error) {
      console.error('Erro ao buscar arquivos:', error)
    } finally {
      setLoading(false)
    }
  }, [folderPath])

  // Carrega os arquivos ao montar o componente
  useEffect(() => {
    fetchFolderContents()
  }, [fetchFolderContents])

  // Função para processar os dados para os gráficos
  const processDataForCharts = (dataSources: DataSource[]): {
  totalStats: TotalStats;
  jobsOverTime: JobTimeData[];
} => {
  const totalStats: TotalStats = {
    scanned: 0,
    indexed: 0,
    modified: 0,
    deleted: 0,
    failed: 0
  };

  const jobsOverTime: JobTimeData[] = [];

  dataSources.forEach(dataSource => {
    dataSource.jobs.forEach(job => {
      totalStats.scanned += job.statistics.numberOfDocumentsScanned;
      totalStats.indexed += job.statistics.numberOfNewDocumentsIndexed;
      totalStats.modified += job.statistics.numberOfModifiedDocumentsIndexed;
      totalStats.deleted += job.statistics.numberOfDocumentsDeleted;
      totalStats.failed += job.statistics.numberOfDocumentsFailed;

      jobsOverTime.push({
        x: new Date(job.startedAt).getTime(),
        y: job.statistics.numberOfDocumentsScanned
      });
    });
  });

  return { totalStats, jobsOverTime };
};



  // Método para listar as fontes de dados
  // Modifique a função listDataSourceByName
const listDataSourceByName = useCallback(async (knowledgeBaseName: string) => {
  try {
    setLoading(true);
    
    // Log para debug
    console.log('KnowledgeBaseName:', knowledgeBaseName);

    const response = await GET(`/knowledge-base/listIngestionJobsForDataSource/${knowledgeBaseName}`);
    
    // Log para debug
    console.log('Response:', response);

    if (!response?.success) {
       Swal.fire({
        icon: 'info',
        title: 'Sem dados',
        text: 'Nenhum histórico de sincronização encontrado.',
        timer: 3000,
      });
      return;
    }

    // Verifica se há dados
    if (!response.data?.dataSources?.length) {
      Swal.fire({
        icon: 'info',
        title: 'Sem dados',
        text: 'Nenhum histórico de sincronização encontrado.',
        timer: 3000,
      });
      return;
    }

    // Organizar o response.data.dataSources por data de atualização da maior para menor separado por data source
    response.data.dataSources.forEach((ds: DataSource) => {
      ds.jobs.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
    });
    
    const { totalStats, jobsOverTime } = processDataForCharts(response.data.dataSources);

    // Log para debug
    console.log('Stats:', totalStats);
    console.log('Jobs:', jobsOverTime);

    const tableContent = response.data.dataSources.map((ds: DataSource) => `
      <!-- Data Source -->
      <tr>
        <td class="fw-bold">${ds.dataSourceName || '-'}</td>
        <td class="text-center">
          <span class="badge badge-light-${ds.status === 'AVAILABLE' ? 'success' : 'warning'} fs-8">
            ${ds.status === 'AVAILABLE' ? i18n.t('fileManagement.available').toUpperCase() : ds.status || '-'}
          </span>
        </td>
        <!--
            <td class="text-center">${ds.jobs?.[0]?.completedAt ? new Date(ds.jobs[0].completedAt).toLocaleString('pt-BR') : '-'}</td>
            <td class="text-center">${ds.jobs?.[0]?.statistics?.numberOfDocumentsScanned || '0'}</td>
            <td class="text-center">${ds.jobs?.[0]?.statistics?.numberOfNewDocumentsIndexed || '0'}</td>
            <td class="text-center">${ds.jobs?.[0]?.statistics?.numberOfModifiedDocumentsIndexed || '0'}</td>
            <td class="text-center">${ds.jobs?.[0]?.statistics?.numberOfDocumentsDeleted || '0'}</td>
            <td class="text-center">${ds.jobs?.[0]?.statistics?.numberOfDocumentsFailed || '0'}</td>
        -->
      </tr>
      ${ds.jobs?.slice(0, 5).map((job: Job) => `
        <tr class="text-gray-600 fs-8 bg-light-secondary">
          <td class="ps-8">Job ${job.ingestionJobId?.slice(-6) || '-'}</td>
          <td class="text-center">
            <span class="badge badge-light-${job.status === 'COMPLETE' ? 'success' : 'warning'} fs-8">
              ${job.status === 'COMPLETE' ? i18n.t('fileManagement.completed') : job.status}
            </span>
          </td>
          <td class="text-center">${job.startedAt ? new Date(job.startedAt).toLocaleString('pt-BR') : '-'}</td>
          <td class="text-center">${job.statistics?.numberOfDocumentsScanned || '0'}</td>
          <td class="text-center">${job.statistics?.numberOfNewDocumentsIndexed || '0'}</td>
          <td class="text-center">${job.statistics?.numberOfModifiedDocumentsIndexed || '0'}</td>
          <td class="text-center">${job.statistics?.numberOfDocumentsDeleted || '0'}</td>
          <td class="text-center">${job.statistics?.numberOfDocumentsFailed || '0'}</td>
        </tr>
      `).join('')}
    `).join('');

    // Mostra o modal com os dados
  await Swal.fire({
  title: i18n.t('fileManagement.history_sync') || 'Histórico de Sincronização',
  html: `
    <div class="container-fluid p-0">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="fs-6 fw-bold text-gray-900">
          <span class="text-muted">${i18n.t('fileManagement.knowledge_base')}: </span>
          ${response.data.knowledgeBaseName}
          <span class="badge badge-light-primary ms-2">${response.data.totalDataSources} Fontes</span>
        </h3>
      </div>
    </div>

    <!-- Gráficos em duas colunas -->
    <div class="row g-3 mb-3">
      <div class="col-6">
        <div class="card card-flush h-100">
          <div class="card-body p-2">
            <canvas id="pieChart" style="height: 200px;"></canvas>
          </div>
        </div>
      </div>
      <div class="col-6">
        <div class="card card-flush h-100">
          <div class="card-body p-2">
            <canvas id="lineChart" style="height: 200px;"></canvas>
          </div>
        </div>
      </div>
    </div>
      <div>
        <h3 class="fs-6 fw-bold text-gray-900">
          <span class="text-muted">${i18n.t('fileManagement.total_jobs_processed')}: </span>
          ${totalStats.scanned + totalStats.indexed + totalStats.modified + totalStats.deleted + totalStats.failed}
        </h3>
        <div class="text-muted fs-7">
          ${i18n.t('fileManagement.jobs_info')}
        </div>
        <div class="d-flex justify-content-between mt-2">
          <div>
            <span class="badge badge-light-success fs-8">
              ${i18n.t('fileManagement.scanned')}: ${totalStats.scanned}
            </span>
            <span class="badge badge-light-primary fs-8 ms-2">
              ${i18n.t('fileManagement.indexed')}: ${totalStats.indexed}
            </span>
          </div>
          <div>
            <span class="badge badge-light-warning fs-8">
              ${i18n.t('fileManagement.modified')}: ${totalStats.modified}
            </span>
            <span class="badge badge-light-danger fs-8 ms-2">
              ${i18n.t('fileManagement.deleted')}: ${totalStats.deleted}
            </span>
            <span class="badge badge-light-secondary fs-8 ms-2">
              ${i18n.t('fileManagement.failed')}: ${totalStats.failed}
            </span>
          </div>
      </div>
      <div class="text-muted fs-7 mt-2">
        ${i18n.t('fileManagement.observation_table_jobs_phase') || 'Observação: A tabela abaixo mostra os últimos 5 jobs de cada fonte de dados, ordenados por data de início.'}
      </div>

      <!-- Tabela -->
      <div class="table-responsive mt-4">
        <table class="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-2">
          <thead>
            <tr class="fw-bold text-muted bg-light fs-7">
              <th class="min-w-150px">${i18n.t('fileManagement.data_source')}</th>
              <th class="min-w-100px text-center">${i18n.t('fileManagement.status_job')}</th>
              <th class="min-w-150px text-center">${i18n.t('fileManagement.last_update')}</th>
              <th class="min-w-80px text-center">${i18n.t('fileManagement.scanned')}</th>
              <th class="min-w-80px text-center">${i18n.t('fileManagement.indexed')}</th>
              <th class="min-w-80px text-center">${i18n.t('fileManagement.modified')}</th>
              <th class="min-w-80px text-center">${i18n.t('fileManagement.deleted')}</th>
              <th class="min-w-80px text-center">${i18n.t('fileManagement.failed')}</th>
            </tr>
          </thead>
          <tbody class="fs-7">
            ${tableContent}
          </tbody>
        </table>
      </div>
    </div>
  `,
  width: '70%', // Reduz o tamanho do modal para 70% da tela
  heightAuto: true, // Permite que a altura do modal se ajuste automaticamente
  showConfirmButton: false, // Remove o botão OK
  showCloseButton: true,
 didOpen: () => {
  const pieCtx = document.getElementById('pieChart') as HTMLCanvasElement;
  const lineCtx = document.getElementById('lineChart') as HTMLCanvasElement;
  
  if (pieCtx && lineCtx) {
    const total = totalStats.scanned + totalStats.indexed + totalStats.modified + 
                 totalStats.deleted + totalStats.failed;
    const getPercentage = (value: number) => ((value / total) * 100).toFixed(1);

    // Gráfico de Pizza (Doughnut)
    // Modifique a configuração do gráfico de pizza no didOpen
new ChartJS(pieCtx, {
  type: 'doughnut',
  data: {
    labels: [
      `${i18n.t('fileManagement.chart_scanned_doc')}: ${totalStats.scanned} - (${getPercentage(totalStats.scanned)}%)`,
      `${i18n.t('fileManagement.chart_indexed_doc')}: ${totalStats.indexed} - (${getPercentage(totalStats.indexed)}%)`,
      `${i18n.t('fileManagement.chart_modified_doc')}: ${totalStats.modified} - (${getPercentage(totalStats.modified)}%)`,
      `${i18n.t('fileManagement.chart_deleted_doc')}: ${totalStats.deleted} - (${getPercentage(totalStats.deleted)}%)`,
      `${i18n.t('fileManagement.chart_failed_doc')}: ${totalStats.failed} - (${getPercentage(totalStats.failed)}%)`
    ],
    datasets: [{
      data: [
        totalStats.scanned,
        totalStats.indexed,
        totalStats.modified,
        totalStats.deleted,
        totalStats.failed
      ],
      backgroundColor: [
        '#50CD89', // verde
        '#3E97FF', // azul
        '#F1BC00', // amarelo
        '#E74C3C', // vermelho
        '#7239EA'  // roxo
      ],
      borderWidth: 1,
      borderColor: '#ffffff'
    }]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: { size: 11 },
          padding: 10 // Adiciona espaço entre as legendas
        }
      },
     datalabels: {
        color: '#000000',
        font: { size: 10, weight: 'bold' },
        formatter: (value: number) => {
          return `${value}\n(${getPercentage(value)}%)`;
        },
        anchor: 'end',
        align: 'top', // Alinha os labels acima das fatias
        offset: 10, // Aumenta a distância do label em relação à fatia
        textAlign: 'center',
        //display: (context: any) => context.dataset.data[context.dataIndex] > 0
        display: false // Remove os labels das fatias do gráfico
      }
    },
    cutout: '55%',
  },
  plugins: [{  // Adicione este plugin personalizado
    id: 'percentageInCenter',
    beforeDraw: function(chart: any) {
      const ctx = chart.ctx;
      ctx.restore();
      const fontSize = 14;
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      
      const total = chart.data.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
      const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
      const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

      // Desenha o total
      ctx.fillStyle = '#333333';
      ctx.fillText(total.toString(), centerX, centerY);

      // Desenha o texto "Total" acima
      ctx.font = '12px sans-serif';
      ctx.fillStyle = '#666666';
      ctx.fillText('Total', centerX, centerY - fontSize - 2);
      ctx.save();
    }
  }]
});

    // Gráfico de Linha
    const last10Jobs = jobsOverTime
      .sort((a, b) => b.x - a.x)
      .slice(0, 10)
      .reverse();

   // Modifique a configuração do gráfico de linha
new ChartJS(lineCtx, {
  type: 'line',
  data: {
    labels: last10Jobs.map(job => 
      new Date(job.x).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    ),
    datasets: [{
      label: i18n.t('fileManagement.chart_docs_processed') || 'Documentos Processados',
      data: last10Jobs.map(job => job.y),
      borderColor: '#3E97FF',
      backgroundColor: '#3E97FF20',
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#3E97FF',
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2
    }]
  },
  options: {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { size: 11, weight: 'bold' }
        }
      },
      tooltip: {
        position: 'nearest', // Posição do tooltip
        mode: 'index', // Modo de tooltip para mostrar todos os pontos no eixo X
        intersect: false, // Não intersecta com o ponto
        yAlign: 'bottom', // Força o tooltip aparecer acima do ponto
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleFont: { size: 12 }, // Tamanho do título do tooltip
        bodyFont: { size: 11 }, // Tamanho do corpo do tooltip
        padding: 10,
        displayColors: false
      },
      datalabels: {
        align: 'top', // Coloca o label acima do ponto
        anchor: 'end', // Âncora no topo do ponto
        offset: 4, // Distância do ponto
        color: '#808080',
        font: { size: 10 },
        formatter: (value: number) => value.toString(),
        display: (context: any) => context.dataIndex === 0 || 
                                 context.dataIndex === context.dataset.data.length - 1
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#E4E6EF',
          drawBorder: false
        },
        ticks: {
          precision: 0,
          font: { size: 10 },
          color: '#808080',
          stepSize: 1 // Força intervalos inteiros
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: { size: 9 }, // Reduz o tamanho da fonte
          color: '#808080',
          maxTicksLimit: 10, // Limita o número de labels no eixo X
          align: 'end', // Alinha o texto ao final
          padding: 8 // Adiciona espaço entre as labels
        }
      }
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 30, // Aumenta o padding superior para os labels
        bottom: 20
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  }
});
  }
}
});

  } catch (error) {
    console.error('Erro ao listar fontes de dados:', error);
    Swal.fire({
      icon: 'error',
      title: 'Erro',
      text: 'Não foi possível carregar o histórico de sincronização.',
      timer: 3000,
    });
  } finally {
    setLoading(false);
  }
}, []);


    // Função para anexar arquivos
    const handleAttachFiles = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
    }
  
    // Função para remover um arquivo específico
    const removeFile = (fileId: string) => {
      setSelectedFiles(prev => prev.filter(file => file.id !== fileId))
    }

    // Função para remover todos os arquivos
    const removeAllFiles = () => {
      setSelectedFiles([])
    }
    
  // Função para fazer upload de todos os arquivos
    const uploadAllFiles = async () => {
      if (selectedFiles.length === 0) return

      // Verificação final antes do upload
      const totalSize = calculateTotalSize(selectedFiles)
      if (totalSize > MAX_TOTAL_SIZE) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao processar os arquivos: excede o limite de 50MB',
        })
        return
      }

      setUploading(true)
      
      try {
        for (const selectedFile of selectedFiles) {
          if (selectedFile.uploaded) continue

          const formData = new FormData()
          formData.append('files', selectedFile.file)
          formData.append('folderPath', folderPath)
          formData.append('bucketName', bucketname || 'iagenerative.ninegrid.com')

          // Debug - para ver o que está sendo enviado
          console.log('Enviando arquivo:', selectedFile.file.name)
          console.log('Para pasta:', folderPath)

          // Simula progresso de upload
          setSelectedFiles(prev => prev.map(f => 
            f.id === selectedFile.id 
              ? { ...f, progress: 50 }
              : f
          ))

          // Chama a API de upload
          const response = await POST('/manager-file/uploadFile', formData)

          if (response.success) {
            setSelectedFiles(prev => prev.map(f => 
              f.id === selectedFile.id 
                ? { ...f, progress: 100, uploaded: true }
                : f
            ))
          } else {
            //aplique Swal.fire para erros de upload
            console.error(`Erro ao fazer upload de ${selectedFile.file.name}:`, response.message)
            Swal.fire({
              icon: 'error',
              title: 'Erro',
              text: `Erro ao fazer upload de ${selectedFile.file.name}: ${response.message}`,
            })

            //throw new Error(`Erro ao fazer upload de ${selectedFile.file.name}`)
          }
        }

        // Mostra sucesso
        Swal.fire({
          icon: 'success',
          title: 'Sucesso',
          text: 'Todos os arquivos foram carregados com sucesso!',
          timer: 2000,
          showConfirmButton: false
        })

        // Atualiza a lista de arquivos após upload
        await fetchFolderContents()
        
        // Fecha o modal após sucesso
    setTimeout(() => {
          setShowUploadModal(false)
          setSelectedFiles([])
        }, 1000)

      } catch (error) {
        console.error('Erro no upload:', error)
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao fazer upload dos arquivos',
        })
      } finally {
        setUploading(false)
      }
    }


  // Função para fechar modal e limpar arquivos
  const closeModal = () => {
    setShowUploadModal(false)
    setSelectedFiles([])
  }

  const handleCreateFolder = async () => {
  if (!newFolderName) return

  try {
    const response = await POST('/manager-file/createFolder', {
      folderName: newFolderName,
      bucketname: bucketname,
      folderPath: folderPath
    })

    if (response.success) {
      Swal.fire({
        icon: 'success',
        title: 'Sucesso',
        text: 'Pasta criada com sucesso!',
        timer: 1500,
        showConfirmButton: false
      })
      
      // Atualiza a lista de pastas
      await fetchFolderContents()
    } else {
      throw new Error(response.message || 'Erro ao criar pasta')
    }
  } catch (error) {
    console.error('Erro ao criar pasta:', error)
    Swal.fire({
      icon: 'error',
      title: 'Erro',
      text: 'Não foi possível criar a pasta. Tente novamente.'
    })
  } finally {
    setNewFolderName('')
  }
 }
  // Função para mudar de página
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  const handleDownload = async (file: FileItem) => {
  try {
    const response = await POST('/manager-file/downloadFile', {
      etag: file.etag,
      key: file.key,
      bucketname: bucketname,
      folderPath: folderPath,
    })

    if (response.success && response.data.data.url) {
      // Criar uma âncora temporária para download
      const link = document.createElement('a')
      link.href = response.data.data.url // URL do S3
      link.target = '_blank' // Abre em nova aba
      link.download = response.data.data.fileName || getFileName(file.key) // Nome do arquivo
      
      // Adiciona a âncora ao documento
      document.body.appendChild(link)
      
      // Simula o clique
      link.click()
      
      // Remove a âncora do documento
      document.body.removeChild(link)

      // Feedback visual de sucesso
      Swal.fire({
        icon: 'success',
        title: 'Sucesso',
        text: 'Download iniciado com sucesso',
        timer: 2500,
        showConfirmButton: false
      })
    } else {
      throw new Error('URL de download não disponível')
    }
  } catch (error) {
    console.error('Erro no download:', error)
    Swal.fire({
      icon: 'error',
      title: 'Erro',
      text: 'Erro ao fazer download do arquivo. Tente novamente.'
    })
  }
  }

  const handleDelete = async (file: FileItem) => {
    try {
      const result = await Swal.fire({
        title: 'Confirmar exclusão',
        text: `Deseja excluir o arquivo "${getFileName(file.key)}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, excluir',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      })

      if (result.isConfirmed) {
        const response = await POST_ADM('/manager-file/deleteFile', {
          etag: file.etag,
          key: file.key,
          bucketname: bucketname,
          folderPath: folderPath,
        })

        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Sucesso',
            text: 'Arquivo excluído com sucesso',
            timer: 1500,
            showConfirmButton: false
          })
          // Atualiza a lista de arquivos
          await fetchFolderContents()
        } else {
          throw new Error(response.message || 'Erro ao excluir arquivo')
        }
      }
    } catch (error) {
      console.error('Erro ao excluir:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Erro ao excluir o arquivo'
      })
    }
  }

  // Adicione estas funções após as outras funções
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Seleciona todos os itens da página atual
      const currentIds = currentFiles.map(file => file.etag)
      setSelectedItems(currentIds)
    } else {
      // Desmarca todos
      setSelectedItems([])
    }
  }

  const handleSelectItem = (etag: string) => {
    setSelectedItems(prev => {
      if (prev.includes(etag)) {
        return prev.filter(id => id !== etag)
      } else {
        return [...prev, etag]
      }
    })
  }

  // Função para mostrar o modal
  const showCreateFolderModal = async () => {
    const { value: folderName } = await Swal.fire({
      title: 'Nova Pasta',
      html: `
        <div class="mb-3">
          <label class="form-label">Digite o nome da nova pasta:</label>
          <input 
            type="text" 
            id="folderName" 
            class="form-control form-control-solid" 
            placeholder="Nome da pasta"
          />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Criar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'btn btn-primary',
        cancelButton: 'btn btn-light-danger'
      },
      preConfirm: () => {
        const folderInput = document.getElementById('folderName') as HTMLInputElement
        const name = folderInput.value
        if (!name) {
          Swal.showValidationMessage('O nome da pasta é obrigatório')
          return false
        }
        return name
      }
    })

    if (folderName) {
      setNewFolderName(folderName)
      handleCreateFolder()
    }
  }


  const getKbByAgenteName = async (agentName?: any) => {
    try {
      const response = await GET(`/knowledge-base/getKnowledgeBaseByAgentId/${agentName}`)
      if (response && response.data) {
        const kbName = response.data.data?.knowledgeBase?.name || response.data.knowledgeBase?.name

      if (kbName) {
        setKnowledgeBaseName(kbName)
        return response.data
      }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Base de conhecimento não encontrada para o agente especificado',
        }).then(() => navigate('/ia/config'))
        return null
      }
    } catch (error) {
      console.error('Erro ao buscar base de conhecimento:', error)
      Swal.fire({
         icon: 'error',
         title: 'Erro',
         text: 'Erros ao buscar base de conhecimento. É necessário possuir um Agente de IA e uma base de conhecimento habilitada',
      }).then(() => navigate('/ia/config'))

    }
    return null
  }

  const verifyPlugins = async () => {
    const templateRes = await GET('/plugintemplate/ia')
    let hasPlugin = false
    let currentAgentName = ''
    if (templateRes && templateRes.data) {
      templateRes.data.forEach(async (template: TemplateType) => {
        if (template.pluginconfig) {
          if (
            template.pluginconfig.content &&
            template.pluginconfig.content.able &&
            template.pluginconfig.content.able !== '0'
          ) {
            currentAgentName = template.pluginconfig.content.agentName
            setAgentName(template.pluginconfig.content.agentName)
            setConfig(template.pluginconfig)
            hasPlugin = true

            const hasKnowledgeBase = await getKbByAgenteName(currentAgentName)
            if (!hasKnowledgeBase) {
              Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'É necessário possuir um Agente de IA e uma base de conhecimento habilitada',
              }).then(() => navigate('/ia/config'))
            }
          }
        }
      })
    }
    return { hasPlugin, agentName: currentAgentName }
  }

  useEffect(() => {
  if (agentName) {
    console.log('agentName atualizado:', agentName);
  }
}, [agentName])

  const init = async () => {
      setLoading(true)
      const hasPlugin = await verifyPlugins()
      if (!hasPlugin) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'É necessário estar com um plugin de IA habilitado',
        }).then(() => navigate('/ia/config'))
      }
      setLoading(false)
    }

    useEffect(() => {
    init()
  }, [])


  return (
    <>
      <PageTitle>Gerenciador de Arquivos - Pastas</PageTitle>
      

      {/* Input de arquivo oculto */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        multiple
        style={{ display: 'none' }}
        accept=".txt,.doc,.docx,.csv,.xls,.xlsx,.pdf;.jpg,.jpeg,.png"
      />

    {/* Cards do topo em flex container */}
    <div className='d-flex gap-6 mb-6'>
      {/* Card de Informações */}
      <KTCard className='flex-grow-1'>
        <KTCardBody className='d-flex align-items-center'>
          <div className='d-flex align-items-center flex-grow-1'>
            <div className='symbol symbol-45px me-5'>
              <i className='ki-duotone ki-folder fs-2x text-primary'>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
            </div>
            <div className='d-flex flex-column'>
              <span className='fw-bold fs-3'>{i18n.t(`fileManagement.title`)}</span>
                <div className='d-flex'>
                  <BreadCrumbs/>
              </div>
            </div>
          </div>
        </KTCardBody>
      </KTCard>
     </div>


     {/* Tabela de arquivos */}
      <KTCard className='file-manager'>
        <KTCardBody>
          {/* Subcabeçalho  tamanho pasta e itens */}
          <div className='d-flex flex-column mb-6'>
                <span className='text-muted fs-3'>{i18n.t(`fileManagement.file_folder`)}: {folderPath}</span>
                <div className='d-flex'>
                <span className='badge badge-light-primary me-2'>{formatBytes(totalSize)}</span>
                <span className='badge badge-light-info'>
                  {totalItems} {totalItems === 1 ? i18n.t(`fileManagement.file_item`) : i18n.t(`fileManagement.file_itens`)}
                </span>
              </div>
            </div>
         {/* Cabeçalho com pesquisa e botões */}
          <div className='d-flex justify-content-between align-items-center mb-5'>
          {/* Campo de pesquisa */}
          <div className='d-flex align-items-center position-relative w-300px min-w-125px'>
            <i className='fas fa-search fs-3 position-absolute ms-4'></i>
            <input
              type='text'
              className='form-control form-control-solid form-control-sm ps-12'
              placeholder='Pesquisar arquivos e pastas'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

      {/* Botões de ação */}
      <div className='d-flex align-items-center'>
        {/* <button
          className='btn btn-light-primary btn-sm me-2 min-w-125px'
          onClick={showCreateFolderModal}
        >
          <i className='fas fa-folder-plus fs-2 me-2'></i>
          {i18n.t(`fileManagement.file_new_folder`)}
        </button> */}
        <button
          className='btn btn-primary btn-sm me-2 min-w-125px' // Adiciona largura mínima
          onClick={() => setShowUploadModal(true)}
        >
          <i className='fas fa-cloud-upload-alt fs-4 me-2'></i>
          {i18n.t(`fileManagement.file_upload`)}
        </button>
        <button
          className='btn btn-light-info btn-sm me-2 min-w-125px'
          onClick={syncfolders3IA}
        >
         <i className='fas fa-brain fs-3 me-2'></i>
         {i18n.t(`fileManagement.file_sync_knowledge_base`)}
        </button>
       <button
          className='btn btn-primary btn-sm me-2 min-w-125px'
          onClick={() => listDataSourceByName(knowledgeBaseName)}
          disabled={loading}
        >
          <i className='fas fa-database fs-4 me-2'></i>
          {i18n.t(`fileManagement.file_history_sync`)}
        </button>
        <button
          className='btn btn-light-success btn-sm min-w-125px' // Adiciona largura mínima
          onClick={fetchFolderContents}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' role='status'></span>
              {i18n.t(`fileManagement.file_Loading`)}
            </>
          ) : (
            <>
              <i className='fas fa-sync-alt fs-3 me-2'></i>
              {i18n.t(`fileManagement.file_update`)}
            </>
          )}
        </button>
      </div>
    </div>


          <div className='table-responsive'>
            <table className='table align-middle table-striped table-row-bordered table-row-gray-100 align-middle gs-0 gy-2'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th className='w-25px'>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={currentFiles.length > 0 && selectedItems.length === currentFiles.length}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  <th className='min-w-150px cursor-pointer text-center'
                    onClick={() => {
                      setSortOrder(sortField === 'key' && sortOrder === 'asc' ? 'desc' : 'asc')
                      setSortField('key')
                    }}
                  >
                    {i18n.t(`fileManagement.file_name`).toUpperCase()}
                    {sortField === 'key' && (
                      <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'} ms-2`}></i>
                    )}
                  </th>
                  <th 
                    className='min-w-100px cursor-pointer text-center'
                    onClick={() => {
                      setSortOrder(sortField === 'size' && sortOrder === 'asc' ? 'desc' : 'asc')
                      setSortField('size')
                    }}
                  >
                    {i18n.t(`fileManagement.file_size`).toUpperCase()}
                    {sortField === 'size' && (
                      <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'} ms-2`}></i>
                    )}
                  </th>
                  <th className='min-w-100px cursor-pointer text-center'
                    onClick={() => {
                      setSortOrder(sortField === 'lastModified' && sortOrder === 'asc' ? 'desc' : 'asc')
                      setSortField('lastModified')
                    }}
                  >
                    {i18n.t(`fileManagement.file_last_modified`).toUpperCase()}
                    {sortField === 'lastModified' && (
                      <i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'} ms-2`}></i>
                    )}
                  </th>
                  <th className='text-end text-center'>{i18n.t(`fileManagement.file_actions`).toUpperCase()}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className='text-center py-4'>
                      {i18n.t(`fileManagement.file_uploading`)}...
                    </td>
                  </tr>
                ) : files.length === 0 ? (
                  <tr>
                    <td colSpan={5} className='text-center py-4'>
                      {i18n.t(`fileManagement.file_not_found`)}
                    </td>
                  </tr>
                ) : (
                  currentFiles.map((file, index) => (
                    <tr key={index} className='border-bottom border-gray-200'>
                      <td>
                        <div className='form-check form-check-sm form-check-custom form-check-solid'>
                          <input 
                            className='form-check-input widget-9-check'
                            type='checkbox'
                            checked={selectedItems.includes(file.etag)}
                            onChange={() => handleSelectItem(file.etag)}
                          />
                        </div>
                      </td>
                      <td className='fw-semibold'>
                        <div className='d-flex justify-content-start flex-column'>
                          <i className='ki-duotone ki-file fs-2 text-primary me-2'></i>
                          <a href='#' className='text-gray-800 text-hover-primary'>
                            {getFileName(file.key)}
                          </a>
                        </div>
                      </td>
                      <td className='text-center'>{formatBytes(file.size)}</td>
                      <td className='text-center'>{formatDate(file.lastModified)}</td>
                      <td className='text-end text-center'>
                        <button 
                          className='btn btn-icon btn-sm btn-light-primary btn-xs me-2'
                          onClick={() => handleDownload(file)}
                          title="Download"
                        >
                          <i className='fas fa-download fs-3'></i>
                        </button>
                        <button 
                          className='btn btn-icon btn-sm btn-light-danger btn-xs'
                          onClick={() => handleDelete(file)}
                          title="Excluir"
                        >
                          <i className='fas fa-trash fs-3'></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Paginação */}
          <div className='d-flex flex-stack flex-wrap pt-10'>
           <div className='fs-6 fw-bold text-gray-700'>
            {i18n.t(`fileManagement.file_page_showing`).toUpperCase()} {indexOfFirstItem + 1} a{' '}
            {Math.min(indexOfLastItem, filteredFiles.length)} de {filteredFiles.length} registros
           </div>
            
            <ul className='pagination pagination-sm'>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className='page-link px-3'
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className='fas fa-angle-left fs-7'></i>
                </button>
              </li>

              {[...Array(totalPages)].map((_, index) => (
                <li
                  key={index + 1}
                  className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                >
                  <button
                    className='page-link px-3'
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              ))}

              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className='page-link px-3'
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <i className='fas fa-angle-right fs-7'></i>
                </button>
              </li>
            </ul>
          </div>
        </KTCardBody>
      </KTCard>

      

      {/* Modal de upload */}
      {/* Modal de upload atualizado */}
      {/* Modal de upload centralizado */}
{showUploadModal && (
  <div className='modal fade show d-block' tabIndex={-1} role='dialog' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
    <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Carregar arquivos</h5>
          <button
            type='button'
            className='btn-close'
            onClick={closeModal}
            disabled={uploading}
          ></button>
        </div>
        <div className='modal-body'>
          {selectedFiles.length === 0 ? (
            <div className='text-center py-4'>
              <i className='ki-duotone ki-files fs-3x text-muted mb-3'>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
              <p className='text-muted'>Nenhum arquivo selecionado</p>
              <p className='text-muted fs-8'>
                Tamanho máximo: <strong>50MB total</strong><br/>
                Tipos permitidos: <strong>TXT, DOC, DOCX, CSV, PDF, XLS, XLSX, JPG, JPEG, PNG</strong>
              </p>
            </div>
          ) : (
            <div>
              <div className='mb-3 d-flex justify-content-between align-items-center'>
                <div>
                  <strong>{selectedFiles.length} arquivo(s) selecionado(s)</strong>
                </div>
                <div className='text-muted'>
                  <strong>Total: {formatBytes(calculateTotalSize(selectedFiles))}</strong>
                  <span className='text-success ms-2'>/ 50MB</span>
                </div>
              </div>
              
              {/* Barra de progresso total */}
              <div className='mb-3'>
                <div className='progress' style={{ height: '8px' }}>
                  <div
                    className={`progress-bar ${calculateTotalSize(selectedFiles) > MAX_TOTAL_SIZE ? 'bg-danger' : 'bg-primary'}`}
                    role='progressbar'
                    style={{ width: `${Math.min((calculateTotalSize(selectedFiles) / MAX_TOTAL_SIZE) * 100, 100)}%` }}
                  ></div>
                </div>
                {calculateTotalSize(selectedFiles) > MAX_TOTAL_SIZE && (
                  <small className='text-danger'>
                    Limite de 50MB excedido
                  </small>
                )}
              </div>

              <div className='max-h-300px overflow-auto'>
                {selectedFiles.map((selectedFile) => (
                  <div key={selectedFile.id} className='border rounded p-3 mb-2'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <div>
                        <strong>{selectedFile.file.name}</strong>
                        <span className='text-muted ms-2'>({formatBytes(selectedFile.file.size)})</span>
                      </div>
                      <button
                        className='btn btn-icon btn-sm btn-light'
                        onClick={() => removeFile(selectedFile.id)}
                        disabled={uploading}
                      >
                        <i className='ki-duotone ki-cross fs-3'></i>
                      </button>
                    </div>
                    {selectedFile.progress > 0 && (
                      <div className='progress mb-2'>
                        <div
                          className={`progress-bar ${selectedFile.uploaded ? 'bg-success' : ''}`}
                          role='progressbar'
                          style={{ width: `${selectedFile.progress}%` }}
                        ></div>
                      </div>
                    )}
                    {selectedFile.uploaded && (
                      <small className='text-success'>
                        <i className='ki-duotone ki-check fs-6 me-1'></i>
                        Upload concluído
                      </small>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className='modal-footer'>
          <button 
            className='btn btn-light-primary'
            onClick={handleAttachFiles}
            disabled={uploading}
          >
            <i className='ki-duotone ki-file-up fs-3 me-2'></i>
            Anexar arquivos
          </button>
          {selectedFiles.length > 0 && (
            <>
              <button 
                className='btn btn-primary'
                onClick={uploadAllFiles}
                disabled={
                  uploading || 
                  selectedFiles.every(f => f.uploaded) || 
                  calculateTotalSize(selectedFiles) > MAX_TOTAL_SIZE
                }
              >
                {uploading ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' role='status'></span>
                    Carregando...
                  </>
                ) : (
                  <>
                    <i className='ki-duotone ki-cloud-upload fs-3 me-2'></i>
                    Carregar tudo
                  </>
                )}
              </button>
              <button 
                className='btn btn-light-danger'
                onClick={removeAllFiles}
                disabled={uploading}
              >
                <i className='ki-duotone ki-trash fs-3 me-2'></i>
                Remover tudo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
)}

{/* ProgressTracker */}
    <ProgressTracker
      processId={syncProcessId}
      checkEndpoint="/knowledge-base/syncCheckStatus"
      onComplete={() => {
        setSyncProcessId(null);
        // Atualiza a lista após conclusão
        fetchFolderContents();
      }}
      onError={(error) => {
        console.error('Erro no processo:', error);
        setSyncProcessId(null);
        Swal.fire({
          icon: 'error',
          title: 'Erro no processo',
          text: 'Ocorreu um erro durante a sincronização.'
        });
      }}
    />
    </>
  )
}

export default FileManagerFilesPage